/**
 * TipTapDocumentPane
 *
 * Enhanced TipTap editor pane with auto-save, track changes, comments, export features.
 * Mirrors DocumentAgentDraft functionality but uses TipTap editor.
 */

import React, {
  useRef,
  useEffect,
  useCallback,
  useState,
  useMemo,
} from "react";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import TipTapEditor, {
  TipTapEditorRef,
  TipTapEditorContentData,
  CommentData,
} from "../../Components/TipTapEditor/TipTapEditor";
import { TrackChangeInfo } from "../../Components/TipTapEditor/extensions/TrackChangesExtension";
import { CommentsPanelRef } from "../../Components/TipTapEditor/CommentsPanel";
import { TrackChangeData } from "../../Components/TipTapEditor/TrackChangesPanel";
import {
  exportToDocxWithTrackChanges,
  downloadDocx,
} from "../../Components/TipTapEditor/utils/docxExport";
import { importDocxToHtml } from "../../Components/TipTapEditor/utils/docxImport";
import {
  importDocxV2,
  uploadDocxImagesToGCSFromJson,
} from "../../Components/TipTapEditor/utils/docxImportV2";
import type { ParsedHeaderFooterConfig } from "../../Components/TipTapEditor/utils/ooxmlParser";
import { normalizeParsedConfig } from "../../Components/TipTapEditor/utils/headerFooterZones";
import {
  PageSetup,
  normalizePageSetup,
  pageSetupKey,
} from "../../Components/TipTapEditor/utils/pageSetup";
import {
  buildPrintHtml,
  openPrintWindow,
} from "../../Components/TipTapEditor/utils/printDocument";
import PageSetupModal from "../../Components/TipTapEditor/PageSetupModal";
import DocumentOutlinePanel from "../../Components/TipTapEditor/DocumentOutlinePanel";
import { useDocumentContext } from "./DocumentAgentLGContext";
import { TipTapDocument, DocumentComment } from "./DocumentAgentLGTypes";
import { useWorkproductContext } from "../../Context/WorkproductContext";
import { useUserContext } from "../../Context/UserContext";
import "./CSS/TipTapDocumentPane.css";

// ============================================================================
// TYPES
// ============================================================================

interface TipTapDocumentPaneProps {
  workproductId: string;
  workproductType?: string;
  docId: string;
  documentName?: string;
  backendUrl?: string;
  onNewDocument?: () => void;
  onImportDocxAsNewTab?: (file: File) => Promise<void>;
  onImportPdfAsNewTab?: (file: File) => Promise<void>;
}

interface TextStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  sentences: number;
  paragraphs: number;
}

// ============================================================================
// COMPONENT
// ============================================================================

const TipTapDocumentPane: React.FC<TipTapDocumentPaneProps> = ({
  workproductId,
  workproductType = "TipTap Document Agent",
  docId,
  documentName,
  backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001",
  onNewDocument,
  onImportDocxAsNewTab,
  onImportPdfAsNewTab,
}) => {
  const { t } = useTranslation();
  const editorRef = useRef<TipTapEditorRef>(null);

  // Check if we're in read-only mode (shared view or documentReadOnly)
  const workproductContext = useWorkproductContext();
  const { documentReadOnly: contextDocReadOnly } = useDocumentContext();
  const isSharedView = workproductContext?.readOnly || contextDocReadOnly;

  // Get current user for comment authorship
  const { user } = useUserContext();
  const currentUserName = user?.username || t("documentEditor.unknownUser");

  // Context
  const {
    registerEditor,
    unregisterEditor,
    getDocumentById,
    updateDocumentContent,
    markDocumentClean,
    saveUnsavedEditorState,
    restoreUnsavedEditorState,
    clearUnsavedEditorState,
    setTrackChangesState,
    getTrackChangesState,
    registerCommentCallback,
    unregisterCommentCallback,
    updateDocumentComments,
    saveDirtyDocuments,
    isAgentWorking,
    conflictedDocIds,
    markDocumentConflicted,
    clearDocumentConflict,
  } = useDocumentContext();

  const hasSaveConflict = conflictedDocIds.has(docId);

  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [_isExporting, setIsExporting] = useState(false);
  const [_isImporting, setIsImporting] = useState(false);
  const [trackChangesEnabled, setTrackChangesEnabled] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [textStats, setTextStats] = useState<TextStats | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  const [confirmRestoreVersion, setConfirmRestoreVersion] = useState<
    number | null
  >(null);
  const versionHistoryRef = useRef<HTMLDivElement>(null);

  // Comments state
  const [comments, setComments] = useState<CommentData[]>([]);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [pendingCommentId, setPendingCommentId] = useState<string | null>(null);
  const [showCommentsPanel, setShowCommentsPanel] = useState(false);
  const commentsPanelRef = useRef<CommentsPanelRef>(null);

  // Track changes state (for navigation and UI)
  const [trackChanges, setTrackChanges] = useState<TrackChangeInfo[]>([]);
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [activeChangeId, setActiveChangeId] = useState<string | null>(null);
  const [showTrackChangesPanel, setShowTrackChangesPanel] = useState(false);

  // Document outline panel
  const [showOutlinePanel, setShowOutlinePanel] = useState(false);

  // Header/footer state (from DOCX import or in-place edits). The TipTap
  // editor owns rendering + in-place editing; this state is the persisted copy.
  const [headerFooterConfig, setHeaderFooterConfig] =
    useState<ParsedHeaderFooterConfig | null>(null);

  // Page size / orientation / margins (File → Page setup, DOCX import).
  // Changing it remounts the editor via a key — content re-hydrates from
  // context (initialContent) or from the pending ref for in-modal applies.
  const [pageSetup, setPageSetup] = useState<PageSetup | null>(null);
  const [showPageSetupModal, setShowPageSetupModal] = useState(false);
  const pendingPageSetupContentRef = useRef<any>(null);

  // Document watermark text (persisted in meta.watermark). No editor remount
  // needed — it's applied via a CSS overlay effect on the prop.
  const [watermark, setWatermark] = useState<string | null>(null);

  // Saves are blocked until the document finished hydrating. Without this,
  // an early save (autosave racing the load, dev-server remounts, …) writes
  // the half-hydrated editor over the server copy — observed losing
  // footnotes and meta.headerFooter. lastServerMetaRef preserves meta keys
  // this component doesn't manage so a save can never drop them.
  const hydratedRef = useRef(false);
  const lastServerMetaRef = useRef<Record<string, any>>({});
  // True server version of this document. The context's version field is
  // unreliable (often stuck at 1), so saves derive their optimistic-
  // concurrency version from here — stale writers get a 409 instead of
  // overwriting newer content.
  const serverVersionRef = useRef(0);

  // Ref to track if we're importing (to prevent state resets)
  const isImportingRef = useRef(false);

  // Ref to suppress dirty-marking when content is set programmatically
  // (e.g. loading from server, restoring on tab switch, etc.)
  const isProgrammaticContentSetRef = useRef(false);

  // Cache for deleted comments - allows restoration on undo
  // When a comment is deleted (either explicitly or by deleting its text), we store it here
  // so it can be restored if the user undoes the deletion
  const deletedCommentsRef = useRef<Map<string, CommentData>>(new Map());

  // When true, the next comment-sync should be treated as a clean baseline
  // (freshly-loaded comments) and must NOT mark the document dirty. Set by the
  // comment-load paths; consumed by the comment-sync effect below. Prevents the
  // spurious "unsaved changes" tab dot that appeared on every tab switch.
  const pendingCommentBaselineRef = useRef(false);

  // Get document from context
  const document = getDocumentById(docId) as TipTapDocument | undefined;

  // Keep the current dirty flag readable inside async callbacks (the load-fetch
  // closure captures a stale `document`). Synced every render.
  const documentDirtyRef = useRef(false);
  documentDirtyRef.current = document?.isDirty === true;

  // Fresh tabs created in-memory (e.g. "Import as new document") carry their
  // imported header/footer in the context document's meta — the server load
  // path only covers documents that already exist in Postgres.
  useEffect(() => {
    const metaHF = (document as any)?.meta?.headerFooter;
    if (metaHF && !headerFooterConfig) {
      setHeaderFooterConfig(normalizeParsedConfig(metaHF));
    }
    const metaPS = (document as any)?.meta?.pageSetup;
    if (metaPS && !pageSetup) {
      setPageSetup(normalizePageSetup(metaPS));
    }
    const metaWM = (document as any)?.meta?.watermark;
    if (typeof metaWM === "string" && watermark === null) {
      setWatermark(metaWM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document]);

  // ============================================================================
  // SYNC COMMENTS TO DOCUMENT CONTEXT (for auto-save persistence)
  // ============================================================================

  useEffect(() => {
    // Don't sync the transient empty state that exists before comments have
    // loaded for this doc. Syncing it would clobber loaded comments and, before
    // this guard, spuriously marked the document dirty on every tab switch
    // (the pane remounts per active tab, so this effect runs on each switch).
    // INVARIANT: pendingCommentBaselineRef is only ever set true alongside a
    // setComments(non-empty) in the load paths, so on the render that consumes
    // a baseline the comments are non-empty and this early return cannot fire.
    // The baseline ref can therefore never be stranded true by this skip.
    if (comments.length === 0 && hasLoadedCommentsRef.current !== docId) {
      return;
    }

    // Convert CommentData[] to DocumentComment[] and sync to context
    // so that saveDirtyDocuments includes comments in the save payload
    const commentsForContext: DocumentComment[] = comments.map(c => ({
      id: c.id,
      content: c.content,
      author: c.author,
      createdAt:
        c.createdAt instanceof Date ? c.createdAt.toISOString() : c.createdAt,
      replies: c.replies?.map(r => ({
        id: r.id,
        content: r.content,
        author: r.author,
        createdAt:
          r.createdAt instanceof Date ? r.createdAt.toISOString() : r.createdAt,
      })),
      // Persist resolved state. Without this, resolving/unresolving a comment
      // (which only changes resolvedAt) serializes identically and the no-op
      // guard in updateDocumentComments would drop it (never dirty, never save).
      resolvedAt:
        c.resolvedAt instanceof Date
          ? c.resolvedAt.toISOString()
          : c.resolvedAt,
    }));

    // Freshly-loaded comments are a clean baseline, not a user edit, so they
    // must not mark the document dirty. Genuine comment edits (add/edit/delete)
    // flow through here with the flag unset and do mark dirty.
    const isBaselineSync = pendingCommentBaselineRef.current;
    pendingCommentBaselineRef.current = false;
    updateDocumentComments(docId, commentsForContext, !isBaselineSync);
  }, [comments, docId, updateDocumentComments]);

  // ============================================================================
  // EDITOR REGISTRATION
  // ============================================================================

  useEffect(() => {
    registerEditor(docId, editorRef);
    return () => {
      unregisterEditor(docId);
    };
  }, [docId, registerEditor, unregisterEditor]);

  // ============================================================================
  // COMMENT CALLBACK REGISTRATION
  // ============================================================================

  useEffect(() => {
    // Register callback to receive new comments from AI operations
    const handleNewComments = (newComments: CommentData[]) => {
      setComments(prev => {
        // Filter out duplicates by ID
        const existingIds = new Set(prev.map(c => c.id));
        const uniqueNew = newComments.filter(c => !existingIds.has(c.id));
        if (uniqueNew.length === 0) return prev;
        return [...prev, ...uniqueNew];
      });
      // Show comments panel if new comments added
      if (newComments.length > 0) {
        setShowCommentsPanel(true);
      }
    };

    registerCommentCallback(docId, handleNewComments);
    return () => {
      unregisterCommentCallback(docId);
    };
  }, [docId, registerCommentCallback, unregisterCommentCallback]);

  // ============================================================================
  // LOAD DOCUMENT
  // ============================================================================

  // Track if we've already loaded comments for this docId
  const hasLoadedCommentsRef = useRef<string | null>(null);

  // Track if we've already fetched comments for this docId (separate from content load)
  const hasFetchedForDocRef = useRef<string | null>(null);

  // Track the docId whose locally-persisted UNSAVED edits we restored on mount.
  // The async load-fetch resolves ~200ms later and would otherwise overwrite the
  // editor with the older server copy — silently reverting edits made just
  // before a tab switch. Set by the restore effect, consulted by the load effect.
  const restoredUnsavedForDocRef = useRef<string | null>(null);

  useEffect(() => {
    const loadDocument = async () => {
      if (!docId) return;

      // Already fetched for this exact docId - skip
      if (hasFetchedForDocRef.current === docId) {
        return;
      }

      const needsContentLoad = !document?.content;

      // If document is genuinely new (version 1 or unset, no real gcsPath, and has
      // content locally), skip server fetch — it won't exist on server yet.
      // Documents with version > 1 have been saved before and may have comments
      // stored in PostgreSQL that we need to fetch.
      // NOTE: We don't check `document?.comments?.length` here because
      // DOCX imports can have locally-available comments (extracted during
      // import) while still being new (never saved to server).
      // NOTE: gcsPath === "hydrated" is a synthetic marker set during
      // hydration from fileContent — it does NOT mean the document exists
      // on the server. Treat it the same as no gcsPath.
      const hasRealGcsPath =
        !!document?.gcsPath && document.gcsPath !== "hydrated";
      const isNewLocalDocument =
        !needsContentLoad && (document?.version ?? 1) <= 1 && !hasRealGcsPath;

      if (isNewLocalDocument) {
        if (editorRef.current && document.htmlContent) {
          // Ensure the editor actually has the htmlContent. There is a race
          // where useEditor may initialise with the empty JSON `content`
          // placeholder before the htmlContent-based `initialContent` is
          // applied. If the editor is still empty, set it explicitly.
          const currentText = editorRef.current.getText().trim();

          if (!currentText) {
            isProgrammaticContentSetRef.current = true;
            editorRef.current.setContent(document.htmlContent);
            isProgrammaticContentSetRef.current = false;
          }

          // Extract the parsed JSON so the actual content (not the empty
          // placeholder) is persisted to the backend, ensuring it survives
          // a page refresh.
          const json = editorRef.current.getJSON();
          const html = editorRef.current.getHTML();
          updateDocumentContent(docId, json, html, document.documentName);

          // Schedule an immediate save so the content is persisted even if the
          // user refreshes before interacting with the chat.
          setTimeout(async () => {
            try {
              await saveDirtyDocuments();
            } catch (e) {}
          }, 200);
        } else if (editorRef.current && document.content) {
          isProgrammaticContentSetRef.current = true;
          editorRef.current.setContent(document.content);
          isProgrammaticContentSetRef.current = false;
        }
        hasFetchedForDocRef.current = docId;
        hydratedRef.current = true;
        return;
      }

      // Only show loading spinner if loading content (not just comments)
      if (needsContentLoad) {
        setIsLoading(true);
      }

      try {
        const response = await fetch(
          `${backendUrl}/api/document/get-document`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ workproductId, docId }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          // Comments may live on the document payload itself (when loaded
          // from PostgreSQL) or on documentMeta (when loaded from GCS
          // workproduct metadata, e.g. freshly uploaded DOCX).
          const serverComments =
            data.document?.comments || data.documentMeta?.comments;

          // Mark as fetched BEFORE setting state
          hasFetchedForDocRef.current = docId;

          // Always load content from the server when fetching — the server
          // is the authoritative source for documents that have been saved.
          // Previously this was gated on `needsContentLoad`, but on refresh
          // the hydration step always fills `document.content` with whatever
          // was in the workproduct's `fileContent` metadata (which may be a
          // stale placeholder without track-change marks), causing
          // `needsContentLoad` to be false and the server content to be
          // silently discarded.
          // Do NOT overwrite the editor when it already holds newer local
          // content than the server: either we just restored persisted unsaved
          // edits on remount (tab switch), or the in-memory doc is still dirty.
          // Overwriting here silently reverts edits made just before the switch.
          // (A genuinely newer server copy — e.g. an agent edit while the user
          // was on another tab — surfaces later as a save 409, handled there.)
          const localIsNewer =
            restoredUnsavedForDocRef.current === docId ||
            documentDirtyRef.current === true;

          if (data.document && editorRef.current && !localIsNewer) {
            // JSON is the authoritative representation — htmlContent is a
            // lossy secondary (and was observed stripping inline atoms like
            // footnote refs and equations when preferred). Fall back to
            // htmlContent ONLY when the JSON is a missing/empty placeholder
            // (legacy mammoth-era DOCX imports stored html only).
            const jsonContent = data.document.json || data.document.content;
            const jsonIsReal =
              jsonContent &&
              Array.isArray(jsonContent.content) &&
              jsonContent.content.length > 0;
            const hydratedFromHtml = !jsonIsReal && !!data.document.htmlContent;
            const contentToSet = jsonIsReal
              ? jsonContent
              : data.document.htmlContent || jsonContent;
            if (contentToSet) {
              isProgrammaticContentSetRef.current = true;
              editorRef.current.setContent(contentToSet);
              isProgrammaticContentSetRef.current = false;

              // If we loaded from htmlContent, extract the parsed JSON and
              // persist it so subsequent loads use real content instead of
              // the empty placeholder.
              if (hydratedFromHtml) {
                const json = editorRef.current.getJSON();
                const html = editorRef.current.getHTML();
                updateDocumentContent(
                  docId,
                  json,
                  html,
                  document?.documentName
                );
                setTimeout(async () => {
                  try {
                    await saveDirtyDocuments();
                  } catch (e) {}
                }, 200);
              }

              // Re-scan for track changes in the freshly loaded content so
              // the panel reflects marks that were persisted on the server.
              setTimeout(() => {
                if (editorRef.current) {
                  const existingChanges = editorRef.current.getTrackChanges();
                  if (existingChanges.length > 0) {
                    setTrackChanges(existingChanges);
                    setShowTrackChangesPanel(true);
                  }
                }
              }, 100);
            }
          }

          // Load comments if present
          if (serverComments && serverComments.length > 0) {
            const loadedComments: CommentData[] = serverComments.map(
              (c: DocumentComment) => ({
                id: c.id,
                content: c.content,
                author: c.author,
                createdAt: new Date(c.createdAt),
                replies: c.replies?.map(r => ({
                  id: r.id,
                  content: r.content,
                  author: r.author,
                  createdAt: new Date(r.createdAt),
                })),
                resolvedAt: c.resolvedAt ? new Date(c.resolvedAt) : undefined,
              })
            );
            setComments(loadedComments);
            setShowCommentsPanel(true);
            hasLoadedCommentsRef.current = docId;
            // Loaded comments are a clean baseline — don't let the sync effect
            // mark the doc dirty when it mirrors them into context.
            pendingCommentBaselineRef.current = true;
          } else {
          }

          // Restore header/footer config from metadata. Normalization
          // accepts both the old flat shape and the section shape; the
          // TipTapEditor applies it to the page decorations via the
          // headerFooterRich prop (no manual updateHeaderContent needed).
          const savedHF =
            data.documentMeta?.headerFooter ||
            data.document?.meta?.headerFooter;
          if (savedHF) {
            setHeaderFooterConfig(normalizeParsedConfig(savedHF));
          }
          // Restore page setup. Setting it remounts the keyed editor, so
          // stash the just-applied authoritative content for the restore
          // effect — the remounted instance must NOT fall back to context
          // initialContent (a potentially lossy/stale snapshot). Skip the
          // remount entirely when the key wouldn't change.
          const savedPS =
            data.documentMeta?.pageSetup || data.document?.meta?.pageSetup;
          if (savedPS) {
            const normalized = normalizePageSetup(savedPS);
            setPageSetup(prev => {
              if (prev && pageSetupKey(prev) === pageSetupKey(normalized)) {
                return prev;
              }
              pendingPageSetupContentRef.current =
                editorRef.current?.getJSON() || null;
              return normalized;
            });
          }

          // Remember the server's true meta keys so saves can merge instead
          // of replace. documentMeta is a FLATTENED projection (meta keys +
          // documentName/version/documentType/editable/comments) — strip the
          // non-meta fields or they get written into the meta column.
          if (data.document?.meta) {
            lastServerMetaRef.current = data.document.meta;
          } else {
            const {
              documentName: _dn,
              version: _v,
              documentType: _dt,
              editable: _ed,
              comments: _cm,
              content: _ct,
              htmlContent: _hc,
              id: _id,
              isDirty: _idirty,
              isLarge: _il,
              lastModified: _lm,
              gcsPath: _gp,
              changesSummary: _cs,
              createdBy: _cb,
              ...pureMeta
            } = data.documentMeta || {};
            lastServerMetaRef.current = pureMeta;
          }
          serverVersionRef.current =
            data.document?.version || data.documentMeta?.version || 0;
          hydratedRef.current = true;
        } else if (response.status === 404) {
          // Document doesn't exist on server yet (newly created) - this is OK
          hasFetchedForDocRef.current = docId;
          hydratedRef.current = true;
        } else {
          console.error("📄 Failed to load document:", response.status);
        }
      } catch (error) {
        console.error("Error loading document:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [
    docId,
    workproductId,
    backendUrl,
    document?.content,
    document?.version,
    document?.gcsPath,
    document?.htmlContent,
    document?.documentName,
    updateDocumentContent,
    saveDirtyDocuments,
  ]);

  // Also load comments from document context if available (fallback for already loaded docs)
  useEffect(() => {
    if (hasLoadedCommentsRef.current === docId) return;
    if (document?.comments && document.comments.length > 0) {
      const loadedComments: CommentData[] = document.comments.map(
        (c: DocumentComment) => ({
          id: c.id,
          content: c.content,
          author: c.author,
          createdAt: new Date(c.createdAt),
          replies: c.replies?.map(r => ({
            id: r.id,
            content: r.content,
            author: r.author,
            createdAt: new Date(r.createdAt),
          })),
          resolvedAt: c.resolvedAt ? new Date(c.resolvedAt) : undefined,
        })
      );
      setComments(loadedComments);
      setShowCommentsPanel(true);
      hasLoadedCommentsRef.current = docId;
      // Loaded comments are a clean baseline — don't let the sync effect mark
      // the doc dirty when it mirrors them into context.
      pendingCommentBaselineRef.current = true;
    }
  }, [docId, document?.comments]);

  // ============================================================================
  // RESTORE UNSAVED STATE
  // ============================================================================

  // Track if we've already attempted restore for this docId
  const hasRestoredRef = useRef<string | null>(null);

  useEffect(() => {
    // Only restore once per docId to avoid re-running on every context update
    if (hasRestoredRef.current === docId) return;

    const unsavedState = restoreUnsavedEditorState(docId);
    if (unsavedState && editorRef.current) {
      isProgrammaticContentSetRef.current = true;
      editorRef.current.setContent(unsavedState.json);
      isProgrammaticContentSetRef.current = false;
      clearUnsavedEditorState(docId);
      // Remember that the editor now holds local edits newer than the server —
      // the load-fetch below must not clobber them with the older server copy.
      restoredUnsavedForDocRef.current = docId;
    }

    // Mark this docId as restored
    hasRestoredRef.current = docId;
  }, [docId, restoreUnsavedEditorState, clearUnsavedEditorState]);

  // ============================================================================
  // RESTORE TRACK CHANGES STATE
  // ============================================================================

  // Track if we've already restored track changes state for this docId
  const hasRestoredTrackChangesRef = useRef<string | null>(null);

  useEffect(() => {
    // Only restore once per docId
    if (hasRestoredTrackChangesRef.current === docId) return;

    // Restore track changes enabled state from context (if available)
    const trackState = getTrackChangesState(docId);
    if (trackState && editorRef.current) {
      if (trackState.enabled) {
        editorRef.current.enableTrackChanges();
        setTrackChangesEnabled(true);
      }
    }

    // Scan the document for existing track change marks.
    // This handles the case where the document was saved with track changes
    // and reloaded on refresh — the marks are in the content but the panel
    // state (trackChanges array, showTrackChangesPanel) was not persisted.
    setTimeout(() => {
      if (editorRef.current) {
        const existingChanges = editorRef.current.getTrackChanges();
        if (existingChanges.length > 0) {
          setTrackChanges(existingChanges);
          setShowTrackChangesPanel(true);
        }
      }
    }, 300);

    hasRestoredTrackChangesRef.current = docId;
  }, [docId, getTrackChangesState]);

  // ============================================================================
  // AUTO-SAVE (debounced)
  // ============================================================================

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const trackChangesRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleContentChange = useCallback(
    (data: TipTapEditorContentData) => {
      // If this change was triggered by a programmatic setContent (load, restore,
      // tab switch), update context content (to reflect TipTap's normalized JSON)
      // but do NOT mark dirty and do NOT start auto-save timer.
      if (isProgrammaticContentSetRef.current) {
        return;
      }

      // Update context immediately
      updateDocumentContent(docId, data.json, data.html, documentName);

      // Save unsaved state for recovery
      saveUnsavedEditorState(docId, data.json, data.html);

      // Debounced auto-save (30 seconds after last edit)
      // Skip when agent is working — we'll save once after agent completes
      if (!isAgentWorking) {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(async () => {
          try {
            await saveDirtyDocuments();
          } catch (e) {}
        }, 30000);
      }

      // Check if document is empty - if so, clear comments and track changes
      const textContent = data.text?.trim() || "";
      if (textContent.length === 0) {
        // Store all current comments in deleted cache before clearing
        setComments(prev => {
          prev.forEach(c => deletedCommentsRef.current.set(c.id, c));
          return [];
        });
        setTrackChanges([]);
        setActiveCommentId(null);
        setActiveChangeId(null);
        setShowCommentsPanel(false);
        setShowTrackChangesPanel(false);
      } else {
        // Sync track changes from document marks (supports undo/redo)
        // This runs immediately to catch undo operations
        if (!isImportingRef.current) {
          const documentChanges = editorRef.current?.getTrackChanges() || [];

          setTrackChanges(prevChanges => {
            // Get IDs of changes in document vs state
            const documentChangeIds = new Set(documentChanges.map(c => c.id));
            const stateChangeIds = new Set(prevChanges.map(c => c.id));

            // Check if there's any difference (changes added or removed)
            const hasNewChanges = documentChanges.some(
              c => !stateChangeIds.has(c.id)
            );
            const hasRemovedChanges = prevChanges.some(
              c => !documentChangeIds.has(c.id)
            );

            // If track changes were restored (undo), show the panel
            if (hasNewChanges && prevChanges.length < documentChanges.length) {
              setShowTrackChangesPanel(true);
            }

            // Only update if there's a difference to avoid unnecessary re-renders
            if (hasNewChanges || hasRemovedChanges) {
              return documentChanges;
            }
            return prevChanges;
          });
        }

        // Sync comments with document marks (supports undo/redo)
        // Get all comment IDs that currently exist as marks in the document
        const documentCommentIds = new Set(
          editorRef.current?.getCommentIds() || []
        );

        setComments(prevComments => {
          // Step 1: Store comments that are being removed (their marks no longer exist)
          // This allows them to be restored on undo
          const commentsToKeep: CommentData[] = [];
          prevComments.forEach(comment => {
            if (documentCommentIds.has(comment.id)) {
              commentsToKeep.push(comment);
            } else {
              // Mark was deleted - store comment in cache for potential undo restoration
              deletedCommentsRef.current.set(comment.id, comment);
            }
          });

          // Step 2: Check if there are marks without corresponding comment data
          // This happens on undo - try to restore from cache
          const existingIds = new Set(commentsToKeep.map(c => c.id));
          const restoredComments: CommentData[] = [];

          documentCommentIds.forEach(markId => {
            if (!existingIds.has(markId)) {
              // This mark exists but we don't have comment data for it
              // Try to restore from deleted cache
              const cachedComment = deletedCommentsRef.current.get(markId);
              if (cachedComment) {
                restoredComments.push(cachedComment);
                deletedCommentsRef.current.delete(markId); // Remove from cache since it's restored
              }
            }
          });

          // If we restored any comments, show the comments panel
          if (restoredComments.length > 0) {
            setShowCommentsPanel(true);
          }

          return [...commentsToKeep, ...restoredComments];
        });
      }
    },
    [
      docId,
      documentName,
      updateDocumentContent,
      saveUnsavedEditorState,
      isAgentWorking,
      saveDirtyDocuments,
    ]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Lock/unlock editor when agent starts/stops working, and save when done
  const prevAgentWorkingRef = useRef(false);
  useEffect(() => {
    const wasWorking = prevAgentWorkingRef.current;
    prevAgentWorkingRef.current = isAgentWorking;

    if (isAgentWorking && !wasWorking) {
      // Agent started — lock the editor
      editorRef.current?.lockForAI();
    } else if (!isAgentWorking && wasWorking) {
      // Agent finished — unlock the editor and save the final state
      editorRef.current?.unlockFromAI();

      // Cancel any pending debounced save (we'll save right now)
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // Save dirty documents now that the agent is done
      saveDirtyDocuments().catch(e => {});
    }
  }, [isAgentWorking, saveDirtyDocuments]);

  // ============================================================================
  // MANUAL SAVE
  // ============================================================================

  const handleSave = useCallback(async () => {
    if (!editorRef.current) return;
    // Never write a half-hydrated editor over the server copy (this wiped
    // footnotes/headers when a save raced the initial load)
    if (!hydratedRef.current) {
      console.warn("[TipTapDocumentPane] save skipped — not hydrated yet");
      return;
    }

    setIsSaving(true);
    try {
      const json = editorRef.current.getJSON();
      const html = editorRef.current.getHTML();
      // Optimistic-concurrency version: the server rejects (409) any save
      // whose version isn't newer than the stored one
      const version =
        Math.max(serverVersionRef.current, document?.version || 0) + 1;

      // Convert comments to serializable format
      const commentsToSave: DocumentComment[] = comments.map(c => ({
        id: c.id,
        content: c.content,
        author: c.author,
        createdAt:
          c.createdAt instanceof Date ? c.createdAt.toISOString() : c.createdAt,
        replies: c.replies?.map(r => ({
          id: r.id,
          content: r.content,
          author: r.author,
          createdAt:
            r.createdAt instanceof Date
              ? r.createdAt.toISOString()
              : r.createdAt,
        })),
        resolvedAt:
          c.resolvedAt instanceof Date
            ? c.resolvedAt.toISOString()
            : c.resolvedAt,
      }));

      const body = {
        workproductId,
        docId,
        documentName: documentName || document?.documentName || "Document",
        documentType: "tiptap",
        document: {
          json,
          html,
          version,
          updated: new Date().toISOString(),
          comments: commentsToSave,
        },
        changesSummary: "Manual save",
        meta: {
          // Merge-over: keys this component doesn't manage survive the save
          ...lastServerMetaRef.current,
          documentName: documentName || document?.documentName,
          ...(headerFooterConfig ? { headerFooter: headerFooterConfig } : {}),
          ...(pageSetup ? { pageSetup } : {}),
          ...(watermark !== null ? { watermark } : {}),
        },
      };

      const response = await fetch(
        `${backendUrl}/api/document-agent/save-document`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        try {
          const respJson = await response.json();
          serverVersionRef.current = respJson?.documentMeta?.version ?? version;
        } catch {
          serverVersionRef.current = version;
        }
        // Pass the server version so context-side savers (autosave, page-close
        // beacon) don't keep sending a stale version that 409s.
        markDocumentClean(docId, json, serverVersionRef.current);
        clearUnsavedEditorState(docId);
        setLastSaved(new Date());
      } else if (response.status === 409) {
        // Stale version — the server has a newer copy than this client
        // believed. Adopt the server's version but NEVER silently overwrite:
        // flag the conflict so the reload banner appears and autosaves stop.
        try {
          const conflictJson = await response.json();
          if (typeof conflictJson?.currentVersion === "number") {
            serverVersionRef.current = conflictJson.currentVersion;
          }
        } catch {}
        markDocumentConflicted(docId);
        console.warn(
          "[TipTapDocumentPane] save rejected as stale (409) — conflict flagged; reload to get the latest"
        );
      }
    } catch (error) {
      console.error("Error saving document:", error);
    } finally {
      setIsSaving(false);
    }
  }, [
    workproductId,
    docId,
    documentName,
    document,
    comments,
    headerFooterConfig,
    pageSetup,
    watermark,
    backendUrl,
    markDocumentClean,
    clearUnsavedEditorState,
    markDocumentConflicted,
  ]);

  // Latest-save ref so deferred callers (header/footer persist, import)
  // never invoke a stale closure.
  const handleSaveRef = useRef<typeof handleSave>(handleSave);
  handleSaveRef.current = handleSave;

  // Apply + persist a watermark change. Deferred save (50ms) lets the state and
  // the handleSave closure update first so the save carries the new value.
  const handleWatermarkChange = useCallback((text: string) => {
    const val = text.trim() ? text.trim() : "";
    setWatermark(val);
    setTimeout(() => handleSaveRef.current?.(), 50);
  }, []);

  // ============================================================================
  // VERSION HISTORY
  // ============================================================================

  // Close version history dropdown when clicking outside
  useEffect(() => {
    if (!showVersionHistory) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        versionHistoryRef.current &&
        !versionHistoryRef.current.contains(event.target as Node)
      ) {
        setShowVersionHistory(false);
      }
    };
    // Use window since "document" is shadowed by our state variable
    window.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showVersionHistory]);

  const handleVersionHistory = useCallback(async () => {
    // If already open, toggle off
    if (showVersionHistory) {
      setShowVersionHistory(false);
      return;
    }

    setIsLoadingVersions(true);
    setShowVersionHistory(true);

    try {
      // Auto-save before showing history to capture latest state
      if (editorRef.current && document?.isDirty) {
        await handleSave();
      }

      const response = await fetch(
        `${backendUrl}/api/document-agent/versions?docId=${encodeURIComponent(docId)}&workproductId=${encodeURIComponent(workproductId)}`,
        {
          credentials: "include",
        }
      );

      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      } else {
        console.error("Failed to fetch versions:", response.statusText);
        setVersions([]);
      }
    } catch (error) {
      console.error("Error fetching version history:", error);
      setVersions([]);
    } finally {
      setIsLoadingVersions(false);
    }
  }, [
    showVersionHistory,
    backendUrl,
    docId,
    workproductId,
    document?.isDirty,
    handleSave,
  ]);

  const handleRestoreVersion = useCallback((version: number) => {
    setConfirmRestoreVersion(version);
  }, []);

  const confirmRestore = useCallback(async () => {
    if (confirmRestoreVersion === null) return;
    const version = confirmRestoreVersion;
    setConfirmRestoreVersion(null);
    setIsRestoringVersion(true);

    try {
      const response = await fetch(
        `${backendUrl}/api/document-agent/restore-version`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ docId, workproductId, version, expectedVersion: Math.max(serverVersionRef.current, document?.version || 0) }),
        }
      );

      if (response.ok) {
        const data = await response.json();

        // Update the editor content with the restored version
        if (editorRef.current && data.content) {
          isProgrammaticContentSetRef.current = true;
          editorRef.current.setContent(data.content);
          isProgrammaticContentSetRef.current = false;
          // Update context
          updateDocumentContent(docId, data.content, data.htmlContent);
          serverVersionRef.current = data.version;
          markDocumentClean(docId, data.content, data.version);
          setLastSaved(new Date());
          // Refresh track changes list so panel reflects the restored document
          // (which may have its own track changes or none)
          setTimeout(() => {
            if (editorRef.current) {
              const changes = editorRef.current.getTrackChanges();
              setTrackChanges(changes);
            }
          }, 100);
        }

        setShowVersionHistory(false);
      } else {
        const err = await response.json();
        if (response.status === 409) markDocumentConflicted(docId);
        console.error("Failed to restore version:", err);
      }
    } catch (error) {
      console.error("Error restoring version:", error);
    } finally {
      setIsRestoringVersion(false);
    }
  }, [
    confirmRestoreVersion,
    backendUrl,
    docId,
    workproductId,
    updateDocumentContent,
    markDocumentClean,
    markDocumentConflicted,
    document?.version,
  ]);

  /**
   * Format a date as a clear timestamp (e.g. "2026-01-12-13:25:34").
   */
  const formatVersionTimestamp = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    const h = String(date.getHours()).padStart(2, "0");
    const mi = String(date.getMinutes()).padStart(2, "0");
    const s = String(date.getSeconds()).padStart(2, "0");
    return `${y}-${mo}-${d}-${h}:${mi}:${s}`;
  }, []);

  // ============================================================================
  // TRACK CHANGES
  // ============================================================================

  // Refresh track changes list from editor
  const refreshTrackChanges = useCallback(() => {
    const changes = editorRef.current?.getTrackChanges() || [];
    setTrackChanges(changes);
    // Adjust index if out of bounds
    if (changes.length === 0) {
      setCurrentChangeIndex(0);
      setActiveChangeId(null);
    } else if (currentChangeIndex >= changes.length) {
      setCurrentChangeIndex(changes.length - 1);
    }
  }, [currentChangeIndex]);

  const toggleTrackChanges = useCallback(() => {
    if (!editorRef.current) return;

    if (trackChangesEnabled) {
      // Turning OFF: Stop tracking new changes, but KEEP existing ones visible
      editorRef.current.disableTrackChanges();
      setTrackChangesState(docId, null);
      // DON'T clear track changes - they should remain visible for review
      // setTrackChanges([]);
      // setCurrentChangeIndex(0);
      // setActiveChangeId(null);
    } else {
      // Turning ON: Start tracking new changes
      editorRef.current.enableTrackChanges();
      setTrackChangesState(docId, {
        enabled: true,
        authorName: currentUserName,
      });
      // Refresh track changes list to pick up any existing marks
      setTimeout(refreshTrackChanges, 100);
    }
    setTrackChangesEnabled(!trackChangesEnabled);
  }, [
    trackChangesEnabled,
    docId,
    setTrackChangesState,
    refreshTrackChanges,
    currentUserName,
  ]);

  // Navigate between track changes
  const handleNavigateChange = useCallback(
    (direction: "prev" | "next") => {
      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      let newIndex: number;
      if (direction === "prev") {
        newIndex = Math.max(0, currentChangeIndex - 1);
      } else {
        newIndex = Math.min(trackChanges.length - 1, currentChangeIndex + 1);
      }
      setCurrentChangeIndex(newIndex);
      const change = trackChanges[newIndex];
      if (change) {
        setActiveChangeId(change.id);
        // Chain: focus first, then select the change text, then scroll into view
        editor
          .chain()
          .focus()
          .setTextSelection({ from: change.from, to: change.to })
          .scrollIntoView()
          .run();
      }
    },
    [currentChangeIndex, trackChanges]
  );

  // Accept individual change
  const handleAcceptChange = useCallback(
    async (changeId: string) => {
      editorRef.current?.acceptChange(changeId);
      // Refresh the track changes list after a short delay
      setTimeout(refreshTrackChanges, 100);

      // Save immediately using handleSave (reads from editor directly, not context)
      // to avoid timing issues with context sync
      try {
        await handleSave();
      } catch (e) {
        console.error("Failed to save after accepting change:", e);
      }
    },
    [refreshTrackChanges, handleSave]
  );

  // Reject individual change
  const handleRejectChange = useCallback(
    async (changeId: string) => {
      editorRef.current?.rejectChange(changeId);
      // Refresh the track changes list after a short delay
      setTimeout(refreshTrackChanges, 100);

      // Save immediately using handleSave (reads from editor directly, not context)
      try {
        await handleSave();
      } catch (e) {
        console.error("Failed to save after rejecting change:", e);
      }
    },
    [refreshTrackChanges, handleSave]
  );

  // Accept all changes
  const acceptAllChanges = useCallback(async () => {
    if (editorRef.current) {
      editorRef.current.acceptAllChanges();
      setTrackChanges([]);
      setCurrentChangeIndex(0);
      setActiveChangeId(null);

      // Save immediately using handleSave - reads content directly from editor
      // (not from context) to avoid saving stale content before context sync
      try {
        await handleSave();
      } catch (e) {
        console.error("Failed to save after accepting all changes:", e);
      }
    }
  }, [handleSave]);

  // Reject all changes
  const rejectAllChanges = useCallback(async () => {
    if (editorRef.current) {
      editorRef.current.rejectAllChanges();
      setTrackChanges([]);
      setCurrentChangeIndex(0);
      setActiveChangeId(null);

      // Save immediately using handleSave - reads content directly from editor
      try {
        await handleSave();
      } catch (e) {
        console.error("Failed to save after rejecting all changes:", e);
      }
    }
  }, [handleSave]);

  // Handle programmatic track-changes state changes (e.g. from applyPatch
  // enabling tracking before AI operations).  Keeps the local UI state
  // in sync with the editor extension's storage.
  const handleTrackingStateChanged = useCallback(
    (enabled: boolean) => {
      setTrackChangesEnabled(enabled);
      if (enabled) {
        setTrackChangesState(docId, {
          enabled: true,
          authorName: "AI Agent",
        });
        // Show the panel so the user sees the incoming changes
        setShowTrackChangesPanel(true);
      }
    },
    [docId, setTrackChangesState]
  );

  // Handle track change activated in editor (when cursor is on tracked text)
  const handleTrackChangeActivated = useCallback(
    (changeId: string | null, type: "insertion" | "deletion" | null) => {
      setActiveChangeId(changeId);
      if (changeId) {
        const index = trackChanges.findIndex(c => c.id === changeId);
        if (index !== -1) {
          setCurrentChangeIndex(index);
        }
      }
    },
    [trackChanges]
  );

  // Cleanup track changes refresh timeout on unmount
  useEffect(() => {
    return () => {
      if (trackChangesRefreshTimeoutRef.current) {
        clearTimeout(trackChangesRefreshTimeoutRef.current);
      }
    };
  }, []);

  // ============================================================================
  // COMMENTS
  // ============================================================================

  // Handle comment activated in editor (when cursor is on commented text)
  const handleCommentActivated = useCallback((commentId: string | null) => {
    setActiveCommentId(commentId);
    // When cursor moves in the editor, clear the active track change
    if (commentId === null) {
      setActiveChangeId(null);
    }
  }, []);

  // Handle add comment from toolbar button
  const handleAddCommentFromToolbar = useCallback(() => {
    const editor = editorRef.current?.getEditor();
    if (!editor) return;

    const { from, to } = editor.state.selection;
    if (from === to) {
      alert(t("documentEditor.selectTextForComment"));
      return;
    }

    // Generate a new comment ID
    const commentId = `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Add the comment mark to the selected text immediately
    editorRef.current?.addComment(commentId);

    // Set this as the pending comment (waiting for user to type content)
    setPendingCommentId(commentId);
    setActiveCommentId(commentId);

    // Show comments panel
    setShowCommentsPanel(true);

    // Focus the comment input after a short delay
    setTimeout(() => {
      commentsPanelRef.current?.focusInput();
    }, 100);

    // Save immediately after adding comment mark to ensure persistence
    // (comment content will be saved when user submits via handleSubmitPendingComment)
    setTimeout(async () => {
      try {
        await saveDirtyDocuments();
      } catch (e) {
        console.error("Failed to save after adding comment mark:", e);
      }
    }, 150);
  }, [saveDirtyDocuments]);

  // Handle adding a comment (from panel)
  const handleAddComment = useCallback(
    async (commentId: string, content: string, author: string) => {
      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      const { from, to } = editor.state.selection;
      if (from === to) {
        alert(t("documentEditor.selectTextForComment"));
        return;
      }

      // Add the comment mark to the selected text
      editorRef.current?.addComment(commentId);

      // Add to comments list
      const newComment: CommentData = {
        id: commentId,
        content,
        author,
        createdAt: new Date(),
        replies: [],
      };
      setComments(prev => [...prev, newComment]);
      setActiveCommentId(commentId);

      // Save immediately after adding comment to ensure persistence
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after adding comment:", e);
        }
      }, 150);
    },
    [saveDirtyDocuments]
  );

  // Handle deleting a comment
  const handleDeleteComment = useCallback(
    async (commentId: string) => {
      // Store the comment in deleted cache before removing (for undo support)
      setComments(prev => {
        const commentToDelete = prev.find(c => c.id === commentId);
        if (commentToDelete) {
          deletedCommentsRef.current.set(commentId, commentToDelete);
        }
        return prev.filter(c => c.id !== commentId);
      });

      // Remove the comment mark from the editor
      editorRef.current?.removeComment(commentId);

      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
      if (pendingCommentId === commentId) {
        setPendingCommentId(null);
      }

      // Save immediately after deleting comment to ensure persistence
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after deleting comment:", e);
        }
      }, 150);
    },
    [activeCommentId, pendingCommentId, saveDirtyDocuments]
  );

  // Handle resolving/unresolving a comment
  const handleResolveComment = useCallback(
    async (commentId: string) => {
      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? {
                ...comment,
                resolvedAt: comment.resolvedAt ? undefined : new Date(),
              }
            : comment
        )
      );

      // Save immediately after resolving comment
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after resolving comment:", e);
        }
      }, 150);
    },
    [saveDirtyDocuments]
  );

  // State for AI edit from comment loading
  const [aiEditingCommentId, setAIEditingCommentId] = useState<string | null>(
    null
  );

  // Handle AI edit from comment — sends comment as instruction to AI, applies edit with track changes
  const handleAIEditFromComment = useCallback(
    async (commentId: string) => {
      const editor = editorRef.current?.getEditor();
      if (!editor) return;

      setAIEditingCommentId(commentId);

      try {
        // 1. Find comment data
        const comment = comments.find(c => c.id === commentId);
        if (!comment) throw new Error("Comment not found");

        // 2. Extract commented text and parent node from ProseMirror doc
        let commentedText = "";
        let parentNodeId: string | null = null;
        let parentText = "";

        editor.state.doc.descendants((node, pos) => {
          if (parentNodeId) return false; // already found
          // Check if this node is a block-level node containing the comment mark
          if (node.isBlock && node.attrs?.id) {
            let hasCommentMark = false;
            const markedTexts: string[] = [];
            node.descendants(child => {
              if (child.isText && child.marks) {
                for (const mark of child.marks) {
                  if (
                    mark.type.name === "comment" &&
                    mark.attrs.commentId === commentId
                  ) {
                    hasCommentMark = true;
                    markedTexts.push(child.text || "");
                  }
                }
              }
            });
            if (hasCommentMark) {
              parentNodeId = node.attrs.id;
              parentText = node.textContent;
              commentedText = markedTexts.join("");
            }
          }
        });

        if (!parentNodeId || !parentText) {
          throw new Error("Could not find commented text in document");
        }

        // 3. Get surrounding context from serialized nodes
        const serializedNodes = editorRef.current?.getDocumentForLLM() || [];
        const nodeIndex = serializedNodes.findIndex(
          (n: any) => n.id === parentNodeId
        );
        const before: string[] = [];
        const after: string[] = [];
        if (nodeIndex > 0) {
          before.push(serializedNodes[nodeIndex - 1]?.content || "");
        }
        if (nodeIndex >= 0 && nodeIndex < serializedNodes.length - 1) {
          after.push(serializedNodes[nodeIndex + 1]?.content || "");
        }

        // 4. Call backend
        const response = await fetch(
          `${backendUrl}/api/document-agent/ai-edit-from-comment`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              workproductId,
              docId,
              nodeId: parentNodeId,
              paragraphText: parentText,
              commentedText,
              commentContent: comment.content,
              replies: (comment.replies || []).map(r => ({
                author: r.author,
                content: r.content,
              })),
              surroundingContext: { before, after },
            }),
          }
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || "AI edit failed");
        }

        const { editedText } = await response.json();

        // 5. Apply the edit with track changes
        const wasTrackChangesEnabled =
          editorRef.current?.isTrackChangesEnabled();
        const previousAuthor = editorRef.current?.getTrackChangesAuthor();
        editorRef.current?.enableTrackChanges();
        editorRef.current?.setTrackChangesAuthor("AI Assistant");

        const result = editorRef.current?.applyOperations({
          operations: [
            {
              op: "edit" as const,
              target: { nodeId: parentNodeId },
              newContent: editedText,
            },
          ],
        });

        // Restore track changes state
        if (!wasTrackChangesEnabled) {
          editorRef.current?.disableTrackChanges();
        }
        if (previousAuthor) {
          editorRef.current?.setTrackChangesAuthor(previousAuthor);
        }

        if (!result?.success) {
          throw new Error("Failed to apply edit to document");
        }

        // 6. Remove comment mark (safety net — may already be gone if text was replaced)
        editorRef.current?.removeComment(commentId);

        // 7. Resolve the comment
        handleResolveComment(commentId);
      } catch (error: any) {
        console.error("AI edit from comment failed:", error);
        alert(
          t("commentsPanel.aiEditError", "AI edit failed. Please try again.")
        );
      } finally {
        setAIEditingCommentId(null);
      }
    },
    [comments, docId, workproductId, backendUrl, handleResolveComment, t]
  );

  // Handle editing a comment
  const handleEditComment = useCallback(
    async (commentId: string, newContent: string) => {
      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? { ...comment, content: newContent }
            : comment
        )
      );

      // Save immediately after editing comment to ensure persistence
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after editing comment:", e);
        }
      }, 150);
    },
    [saveDirtyDocuments]
  );

  // Handle replying to a comment
  const handleReplyToComment = useCallback(
    async (commentId: string, content: string, author: string) => {
      const replyId = `reply-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const reply: CommentData = {
        id: replyId,
        content,
        author,
        createdAt: new Date(),
      };

      setComments(prev =>
        prev.map(comment =>
          comment.id === commentId
            ? { ...comment, replies: [...(comment.replies || []), reply] }
            : comment
        )
      );

      // Save immediately after replying to comment to ensure persistence
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after replying to comment:", e);
        }
      }, 150);
    },
    [saveDirtyDocuments]
  );

  // Handle clicking a comment in the panel
  const handleCommentClick = useCallback((commentId: string) => {
    setActiveCommentId(prev => (prev === commentId ? null : commentId));
    setActiveChangeId(null);
    // Scroll to and highlight the commented text in the editor
    editorRef.current?.scrollToComment(commentId);
  }, []);

  // Handle canceling a pending comment
  const handleCancelPendingComment = useCallback(() => {
    if (pendingCommentId) {
      editorRef.current?.removeComment(pendingCommentId);
      setPendingCommentId(null);

      // Save immediately after canceling to persist the removed comment mark
      setTimeout(async () => {
        try {
          await saveDirtyDocuments();
        } catch (e) {
          console.error("Failed to save after canceling comment:", e);
        }
      }, 150);
    }
  }, [pendingCommentId, saveDirtyDocuments]);

  // Handle submitting a pending comment
  const handleSubmitPendingComment = useCallback(
    async (content: string) => {
      if (pendingCommentId && content.trim()) {
        const newComment: CommentData = {
          id: pendingCommentId,
          content: content.trim(),
          author: currentUserName,
          createdAt: new Date(),
          replies: [],
        };
        setComments(prev => [...prev, newComment]);
        setPendingCommentId(null);

        // Save immediately after submitting comment to ensure persistence
        setTimeout(async () => {
          try {
            await saveDirtyDocuments();
          } catch (e) {
            console.error("Failed to save after submitting comment:", e);
          }
        }, 150);
      }
    },
    [pendingCommentId, currentUserName, saveDirtyDocuments]
  );

  // Toggle comments panel visibility
  const toggleCommentsPanel = useCallback(() => {
    setShowCommentsPanel(prev => !prev);
  }, []);

  // Toggle track changes panel visibility
  const toggleTrackChangesPanel = useCallback(() => {
    setShowTrackChangesPanel(prev => !prev);
  }, []);

  // Auto-zoom to fit when outline sidebar opens or closes
  // (Comments & track changes now use floating cards that don't affect layout width)
  useEffect(() => {
    const timer = setTimeout(() => {
      editorRef.current?.zoomToFit();
    }, 250);
    return () => clearTimeout(timer);
  }, [showOutlinePanel]);

  // Handle click on a track change in the panel (navigate to it in editor)
  const handleTrackChangeClick = useCallback(
    (changeId: string) => {
      setActiveChangeId(prev => (prev === changeId ? null : changeId));
      setActiveCommentId(null);
      const index = trackChanges.findIndex(c => c.id === changeId);
      if (index !== -1) {
        setCurrentChangeIndex(index);
      }
      // Scroll to and highlight the track change text in the editor
      editorRef.current?.scrollToTrackChange(changeId);
    },
    [trackChanges]
  );

  // Convert TrackChangeInfo to TrackChangeData for the panel
  const trackChangesForPanel: TrackChangeData[] = useMemo(() => {
    return trackChanges.map(tc => ({
      id: tc.id,
      type: tc.type,
      author: tc.author,
      date: tc.date,
      text: tc.text,
    }));
  }, [trackChanges]);

  // ============================================================================
  // EXPORT TO DOCX
  // ============================================================================

  const handleExportDocx = useCallback(async () => {
    const editor = editorRef.current?.getEditor();
    if (!editor) {
      alert(t("documentEditor.noContentToExport"));
      return;
    }

    const json = editor.getJSON();
    const docName = documentName || document?.documentName || "document";

    setIsExporting(true);
    try {
      // Generate DOCX directly in browser (include comments with replies)
      // Build header/footer config for export
      const hfExportConfig = headerFooterConfig
        ? {
            headerLeft: headerFooterConfig.defaultHeader?.left || "",
            headerCenter: headerFooterConfig.defaultHeader?.center || "",
            headerRight: headerFooterConfig.defaultHeader?.right || "",
            footerLeft: headerFooterConfig.defaultFooter?.left || "",
            footerCenter: headerFooterConfig.defaultFooter?.center || "",
            footerRight: headerFooterConfig.defaultFooter?.right || "{page}",
            differentFirstPage: headerFooterConfig.hasTitlePage,
            firstPageHeaderLeft: headerFooterConfig.firstPageHeader?.left || "",
            firstPageHeaderCenter:
              headerFooterConfig.firstPageHeader?.center || "",
            firstPageHeaderRight:
              headerFooterConfig.firstPageHeader?.right || "",
            firstPageFooterLeft: headerFooterConfig.firstPageFooter?.left || "",
            firstPageFooterCenter:
              headerFooterConfig.firstPageFooter?.center || "",
            firstPageFooterRight:
              headerFooterConfig.firstPageFooter?.right || "",
          }
        : undefined;

      const blob = await exportToDocxWithTrackChanges(json, {
        author: t("documentEditor.documentAuthor"),
        title: docName,
        pageSetup: normalizePageSetup(pageSetup),
        comments: comments.map(c => ({
          id: c.id,
          content: c.content,
          author: c.author,
          createdAt: c.createdAt,
          replies: c.replies?.map(r => ({
            id: r.id,
            content: r.content,
            author: r.author,
            createdAt: r.createdAt,
          })),
          // Carry resolved state so resolved comments are excluded from export.
          resolvedAt: c.resolvedAt,
        })),
        headerFooter: hfExportConfig,
      });

      // Download the file
      downloadDocx(blob, `${docName}.docx`);
    } catch (error: any) {
      console.error("Export error:", error);
      alert(t("documentEditor.exportFailed", { message: error.message }));
    } finally {
      setIsExporting(false);
    }
    // headerFooterConfig was missing here for a long time — exports silently
    // dropped headers/footers loaded after the first render (stale closure).
  }, [
    documentName,
    document?.documentName,
    comments,
    headerFooterConfig,
    pageSetup,
  ]);

  // ============================================================================
  // IMPORT FROM DOCX
  // ============================================================================

  const handleImportPdf = useCallback(
    async (file: File) => {
      // If a callback is provided, use it to create a new tab
      if (onImportPdfAsNewTab) {
        setIsImporting(true);
        try {
          await onImportPdfAsNewTab(file);
        } catch (error: any) {
          console.error("PDF import error:", error);
          alert(t("documentEditor.importFailed", { message: error.message }));
        } finally {
          setIsImporting(false);
        }
      } else {
        alert(t("documentEditor.pdfImportNotAvailable"));
      }
    },
    [onImportPdfAsNewTab]
  );

  const handleImportDocx = useCallback(
    async (file: File) => {
      if (
        file.name.toLowerCase().endsWith(".doc") &&
        !file.name.toLowerCase().endsWith(".docx")
      ) {
        alert(
          `"${file.name}" is a legacy .doc file and is not supported. Please save it as .docx and try again.`
        );
        return;
      }
      // If a callback is provided, use it to create a new tab (same behavior as plus button)
      if (onImportDocxAsNewTab) {
        setIsImporting(true);
        try {
          await onImportDocxAsNewTab(file);
        } catch (error: any) {
          console.error("Import error:", error);
          alert(t("documentEditor.importFailed", { message: error.message }));
        } finally {
          setIsImporting(false);
        }
        return;
      }

      // Fallback: overwrite current document (legacy behavior)
      setIsImporting(true);
      isImportingRef.current = true; // Set flag to prevent track changes reset
      try {
        // Feature flag: use custom OOXML parser (V2) or mammoth (V1)
        // V2 reads raw OOXML directly — no marker injection or HTML round-trips.
        // V1 (mammoth) path kept as fallback; set to false to revert.
        const USE_CUSTOM_PARSER = true;

        if (USE_CUSTOM_PARSER) {
          // V2: Custom OOXML-to-ProseMirror parser (direct JSON, no HTML round-trip)
          const result = await importDocxV2(file);

          // Upload base64 images to GCS before setting content
          // (mutates result.doc in place — replaces src with signed URLs)
          try {
            await uploadDocxImagesToGCSFromJson(
              result.doc,
              docId,
              backendUrl,
              workproductId
            );
          } catch (imgErr) {}

          // Save original DOCX to GCS (fire-and-forget, non-blocking)
          try {
            const arrayBuf = await file.arrayBuffer();
            const base64 = btoa(
              new Uint8Array(arrayBuf).reduce(
                (data, byte) => data + String.fromCharCode(byte),
                ""
              )
            );
            fetch(`${backendUrl}/api/document-agent/save-original-docx`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                workproductId,
                docId,
                fileName: file.name,
                fileBase64: base64,
              }),
            })
              .then(resp => {})
              .catch(err => {});

            // (Removed 2026-07-11) The imported DOCX is no longer copied into a
            // shared parser-test corpus — that leaked customer documents
            // cross-tenant. See server.ts corpus-endpoint removal note.
          } catch (saveErr) {}

          // Set ProseMirror JSON directly — no HTML intermediate
          editorRef.current?.setContent(result.doc);

          // Apply imported header/footer configuration. The TipTapEditor
          // re-renders page decorations when the headerFooterRich prop
          // changes — no manual updateHeaderContent calls needed.
          if (result.headerFooter) {
            setHeaderFooterConfig(normalizeParsedConfig(result.headerFooter));
          }
          if (result.pageSetup) {
            // Remounts the editor; the imported content re-applies via the
            // pending ref (context content may not be committed yet)
            pendingPageSetupContentRef.current = result.doc;
            setPageSetup(normalizePageSetup(result.pageSetup));
          }

          // Add imported comments to the comments panel (with nested replies)
          if (result.comments.length > 0) {
            const mapImportedComment = (c: any): CommentData => ({
              id: c.id,
              content: c.content,
              author: c.author,
              createdAt: new Date(c.date || Date.now()),
              replies: c.replies?.map(mapImportedComment),
            });
            const newComments: CommentData[] =
              result.comments.map(mapImportedComment);
            setComments(prev => [...prev, ...newComments]);
            setShowCommentsPanel(true);
          }

          // Handle imported track changes
          if (result.trackChanges && result.trackChanges.length > 0) {
            const convertedChanges: TrackChangeInfo[] = result.trackChanges.map(
              (tc, index) => ({
                id: tc.id,
                type: tc.type,
                from: index,
                to: index + 1,
                text: tc.text,
                author: tc.author,
                date: tc.date,
              })
            );
            setTrackChanges(convertedChanges);
            setShowTrackChangesPanel(true);
            setTimeout(() => {
              isImportingRef.current = false;
            }, 200);
          } else {
            isImportingRef.current = false;
          }
        } else {
          // V1: Mammoth-based pipeline (HTML output)
          const result = await importDocxToHtml(file);

          // Set the HTML content in editor (includes comment and track change spans)
          editorRef.current?.setContent(result.html);

          // Add imported comments to the comments panel
          if (result.comments.length > 0) {
            const newComments: CommentData[] = result.comments.map(c => ({
              id: c.id,
              content: c.content,
              author: c.author,
              createdAt: new Date(c.date || Date.now()),
            }));
            setComments(prev => [...prev, ...newComments]);
            setShowCommentsPanel(true);
          }

          // Handle imported track changes
          if (result.trackChanges && result.trackChanges.length > 0) {
            // Convert ImportedTrackChange to TrackChangeInfo format for the panel
            const convertedChanges: TrackChangeInfo[] = result.trackChanges.map(
              (tc, index) => ({
                id: tc.id,
                type: tc.type,
                from: index, // Position not available from import, use index as placeholder
                to: index + 1,
                text: tc.text,
                author: tc.author,
                date: tc.date,
              })
            );

            // The HTML from the new pipeline contains properly formatted
            // <span data-change-type="insertion|deletion" ...> elements at the
            // correct positions. TipTap's parseHTML rules in TrackChangesExtension
            // recognize these spans and apply the corresponding marks during
            // setContent(). No second pass with applyTrackChangeMark() is needed.

            // Set the state for the panel
            setTrackChanges(convertedChanges);
            setShowTrackChangesPanel(true);

            // Clear the importing flag after a short delay
            setTimeout(() => {
              isImportingRef.current = false;
            }, 200);
          } else {
            // No track changes, clear the flag
            isImportingRef.current = false;
          }
        }

        // Auto-save after import so the document persists across refresh
        setTimeout(() => {
          handleSave();
        }, 500);
      } catch (error: any) {
        console.error("Import error:", error);
        alert(t("documentEditor.importFailed", { message: error.message }));
        isImportingRef.current = false;
      } finally {
        setIsImporting(false);
      }
    },
    [onImportDocxAsNewTab, handleSave]
  );

  // ============================================================================
  // TEXT STATISTICS
  // ============================================================================

  const calculateTextStats = useCallback(() => {
    if (!editorRef.current) return;

    const text = editorRef.current.getText();
    const html = editorRef.current.getHTML();

    const characters = text.length;
    const charactersNoSpaces = text.replace(/\s/g, "").length;
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const sentences = (text.match(/[.!?]+/g) || []).length || 1;
    const paragraphs = (html.match(/<p[^>]*>/gi) || []).length || 1;

    setTextStats({
      characters,
      charactersNoSpaces,
      words,
      sentences,
      paragraphs,
    });
    setShowStats(true);
  }, []);

  // ============================================================================
  // DOCUMENT OUTLINE
  // ============================================================================

  const toggleOutlinePanel = useCallback(() => {
    setShowOutlinePanel(prev => !prev);
  }, []);

  // ============================================================================
  // EXPORT TO PDF
  // ============================================================================

  // Shared builder for Print and Export-to-PDF: a standalone window with
  // ONLY the document (no app UI), @page sized from the page setup, footnote
  // numbers injected, and header/footer bars repeated on every printed page.
  const buildPrintDocumentHtml = useCallback((): string | null => {
    const ref = editorRef.current;
    if (!ref) return null;
    return buildPrintHtml({
      contentHtml: ref.getHTML(),
      title: documentName || document?.documentName || "Document",
      setup: normalizePageSetup(pageSetup),
      headerFooter: headerFooterConfig,
      footnotes: ref.getFootnotes(),
      watermark,
    });
  }, [
    documentName,
    document?.documentName,
    pageSetup,
    headerFooterConfig,
    watermark,
  ]);

  const handleExportPdf = useCallback(async () => {
    // Print-window path: the browser's print engine paginates the FULL
    // document and "Save as PDF" writes the file — replaces the html2canvas
    // capture that clipped long documents to the viewport (BUG-001).
    const html = buildPrintDocumentHtml();
    if (!html) {
      alert(t("documentEditor.noContentToExport"));
      return;
    }
    if (!openPrintWindow(html, { autoPrint: true })) {
      alert(
        t(
          "tiptapEditor.pageSetup.popupBlocked",
          "Please allow pop-ups for this site to print or save as PDF."
        )
      );
    }
  }, [buildPrintDocumentHtml, t]);

  // ============================================================================
  // PRINT
  // ============================================================================

  // Header/footer in-place edit finished — persist the new config.
  // The TipTapEditor already applied it to the page decorations (live
  // preview); here we store it and schedule a save so it lands in meta.
  const handleHeaderFooterRichChange = useCallback(
    (config: ParsedHeaderFooterConfig) => {
      setHeaderFooterConfig(config);
      // Save after React commits the state update so handleSave's closure
      // (via the ref) sees the new config.
      setTimeout(() => handleSaveRef.current?.(), 400);
    },
    []
  );

  const handlePrint = useCallback(() => {
    // Clean print window with only the document — fixes the old behavior
    // where window.print() on the app page leaked nav/chat UI and clipped
    // to a single page (BUG-002).
    const html = buildPrintDocumentHtml();
    if (!html) return;
    if (!openPrintWindow(html, { autoPrint: true })) {
      alert(
        t(
          "tiptapEditor.pageSetup.popupBlocked",
          "Please allow pop-ups for this site to print or save as PDF."
        )
      );
    }
  }, [buildPrintDocumentHtml, t]);

  // Page setup applied from the modal — capture live content first (context
  // can lag behind unsaved keystrokes), remount the keyed editor, re-apply
  // the content, then persist.
  const handleApplyPageSetup = useCallback((setup: PageSetup) => {
    pendingPageSetupContentRef.current = editorRef.current?.getJSON() || null;
    setPageSetup(setup);
    setTimeout(() => handleSaveRef.current?.(), 600);
  }, []);

  // Re-apply captured content into the freshly remounted editor
  useEffect(() => {
    const json = pendingPageSetupContentRef.current;
    if (!pageSetup || !json) return;
    pendingPageSetupContentRef.current = null;
    let tries = 0;
    const apply = () => {
      if (editorRef.current) {
        editorRef.current.setContent(json);
      } else if (tries++ < 10) {
        setTimeout(apply, 100);
      }
    };
    setTimeout(apply, 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSetup]);

  // ============================================================================
  // INITIAL CONTENT
  // ============================================================================

  const initialContent = useMemo(() => {
    // JSON-first, same as loadDocument hydration: HTML is a lossy projection
    // (drops inline atoms like equations and footnote refs), so it is only a
    // fallback for legacy docs whose JSON is an empty placeholder.
    const json = document?.content;
    const jsonIsReal =
      json && Array.isArray(json.content) && json.content.length > 0;
    if (jsonIsReal) return json;
    if (document?.htmlContent) return document.htmlContent;
    if (json) return json;
    return "";
  }, [document?.content, document?.htmlContent]);

  // ============================================================================
  // IMAGE UPLOAD (optimistic: base64 preview → background GCS upload)
  // ============================================================================

  const handleImageUpload = useCallback(
    async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const resp = await fetch(
        `${backendUrl}/api/workproduct-image-upload/${workproductId}`,
        { method: "POST", credentials: "include", body: form }
      );
      if (!resp.ok) throw new Error("Image upload failed");
      const { gcsPath, proxyUrl } = await resp.json();
      return { src: `${backendUrl}${proxyUrl}`, gcsFileName: gcsPath };
    },
    [backendUrl, workproductId]
  );

  // Resolve a save conflict by reloading the authoritative server copy
  // (discarding the local edits that couldn't be saved). This is the
  // block-and-notify "take latest" action surfaced in the conflict banner.
  const handleReloadConflict = useCallback(async () => {
    if (!editorRef.current) return;
    try {
      const resp = await fetch(`${backendUrl}/api/document/get-document`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ workproductId, docId }),
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const serverJson = data.document?.json || data.document?.content;
      if (serverJson) {
        isProgrammaticContentSetRef.current = true;
        editorRef.current.setContent(serverJson);
        isProgrammaticContentSetRef.current = false;
      }
      restoredUnsavedForDocRef.current = null;
      clearUnsavedEditorState(docId);
      markDocumentClean(docId, serverJson, data.documentMeta?.version);
      clearDocumentConflict(docId);
    } catch {
      // Leave the conflict banner up so the user can retry.
    }
  }, [
    backendUrl,
    workproductId,
    docId,
    clearUnsavedEditorState,
    markDocumentClean,
    clearDocumentConflict,
  ]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (isLoading) {
    return (
      <div className="tiptap-document-pane">
        <div className="tiptap-document-pane-loading">
          <Spinner animation="border" size="sm" />
          <span>{t("documentEditor.loadingDocumentSpinner")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tiptap-document-pane">
      {hasSaveConflict && (
        <div className="tiptap-save-conflict-banner" role="alert">
          <span>
            {t("documentEditor.saveConflictMessage", {
              defaultValue:
                "This document was changed elsewhere, so your latest edits couldn't be saved. Reload to get the latest version (your unsaved changes here will be discarded).",
            })}
          </span>
          <button
            type="button"
            className="tiptap-save-conflict-reload"
            onClick={handleReloadConflict}
          >
            {t("documentEditor.saveConflictReload", {
              defaultValue: "Reload latest",
            })}
          </button>
        </div>
      )}
      {/* Editor + Comments Panel */}
      <div className="tiptap-document-pane-body">
        {/* Editor */}
        <div className="tiptap-document-pane-editor">
          <TipTapEditor
            key={`editor-${docId}-${pageSetup ? pageSetupKey(pageSetup) : "default"}`}
            ref={editorRef}
            initialContent={initialContent}
            onChange={handleContentChange}
            onCommentActivated={handleCommentActivated}
            onTrackChangeActivated={handleTrackChangeActivated}
            onTrackingStateChanged={handleTrackingStateChanged}
            showToolbar={true}
            readOnly={isSharedView}
            className="document-agent-tiptap-editor"
            // Save
            onSave={handleSave}
            isSaving={isSaving}
            // showUnsavedBadge={document?.isDirty} // TODO: re-enable once isDirty false positives are resolved
            // Track changes
            trackChangesEnabled={trackChangesEnabled}
            onToggleTrackChanges={toggleTrackChanges}
            trackChanges={trackChanges}
            currentChangeIndex={currentChangeIndex}
            onNavigateChange={handleNavigateChange}
            onAcceptChange={handleAcceptChange}
            onRejectChange={handleRejectChange}
            onAcceptAllChanges={acceptAllChanges}
            onRejectAllChanges={rejectAllChanges}
            // Comments panel
            showCommentsPanel={showCommentsPanel}
            onToggleCommentsPanel={toggleCommentsPanel}
            commentsCount={comments.length}
            onAddComment={handleAddCommentFromToolbar}
            // Track changes panel
            showTrackChangesPanel={showTrackChangesPanel}
            onToggleTrackChangesPanel={toggleTrackChangesPanel}
            trackChangesCount={trackChanges.length}
            // File menu
            onNewDocument={onNewDocument}
            onImportDocx={handleImportDocx}
            onImportPdf={handleImportPdf}
            onExportDocx={handleExportDocx}
            onExportPdf={handleExportPdf}
            onPrint={handlePrint}
            // Document outline
            showOutlinePanel={showOutlinePanel}
            onToggleOutlinePanel={toggleOutlinePanel}
            // Text statistics
            onShowTextStats={calculateTextStats}
            // Version history
            onVersionHistory={handleVersionHistory}
            // Image upload (optimistic rendering)
            onImageUpload={handleImageUpload}
            // Track changes author
            currentUser={currentUserName}
            // Header/footer: rich in-place editing (click a page header or
            // footer to edit; the editor renders zones + live preview)
            headerFooterRich={headerFooterConfig}
            onHeaderFooterRichChange={handleHeaderFooterRichChange}
            // Page setup (size/orientation/margins) — applied via remount
            pageSetup={pageSetup}
            onPageSetup={() => setShowPageSetupModal(true)}
            // Document watermark (DRAFT / CONFIDENTIAL …) — persisted in meta
            watermark={watermark}
            onWatermarkChange={handleWatermarkChange}
            // ── Floating annotations (Google Docs-style margin cards) ──
            floatingComments={comments}
            floatingActiveCommentId={activeCommentId}
            floatingShowComments={true}
            floatingAiEditingCommentId={aiEditingCommentId}
            floatingOnCommentClick={handleCommentClick}
            floatingOnDeleteComment={handleDeleteComment}
            floatingOnResolveComment={handleResolveComment}
            floatingOnEditComment={handleEditComment}
            floatingOnReplyToComment={handleReplyToComment}
            floatingOnAIEditFromComment={handleAIEditFromComment}
            floatingTrackChanges={trackChangesForPanel}
            floatingActiveChangeId={activeChangeId}
            floatingShowTrackChanges={true}
            floatingOnChangeClick={handleTrackChangeClick}
            floatingOnAcceptChange={handleAcceptChange}
            floatingOnRejectChange={handleRejectChange}
            floatingPendingCommentId={pendingCommentId}
            floatingOnSubmitPendingComment={handleSubmitPendingComment}
            floatingOnCancelPendingComment={handleCancelPendingComment}
            floatingOnDismiss={() => {
              setActiveCommentId(null);
              setActiveChangeId(null);
            }}
          />

          {/* Page Setup Modal */}
          <PageSetupModal
            show={showPageSetupModal}
            onHide={() => setShowPageSetupModal(false)}
            initialSetup={pageSetup}
            onApply={handleApplyPageSetup}
          />

          {/* Version History Dropdown */}
          {showVersionHistory && (
            <div className="version-history-overlay" ref={versionHistoryRef}>
              <div className="version-history-header">
                <span className="version-history-title">
                  {t("documentEditor.versionHistory")}
                </span>
                <button
                  className="version-history-close"
                  onClick={() => setShowVersionHistory(false)}
                >
                  &times;
                </button>
              </div>
              <div className="version-history-list">
                {isLoadingVersions ? (
                  <div className="version-history-loading">
                    <Spinner animation="border" size="sm" />{" "}
                    {t("documentEditor.loadingVersions")}
                  </div>
                ) : versions.length === 0 ? (
                  <div className="version-history-empty">
                    {t("documentEditor.noVersionsFound")}
                  </div>
                ) : (
                  versions.map((v: any, idx: number) => (
                    <div
                      key={`v-${v.version}`}
                      className={`version-history-item ${v.isCurrent ? "current" : ""}`}
                    >
                      <div className="version-history-item-row">
                        <span className="version-history-item-version">
                          {v.createdAt
                            ? formatVersionTimestamp(v.createdAt)
                            : `v${v.version}`}
                          {v.isCurrent && (
                            <span className="version-history-current-badge">
                              {t("documentEditor.currentVersion")}
                            </span>
                          )}
                        </span>
                        {!v.isCurrent && (
                          <button
                            className="version-history-restore-btn"
                            onClick={() => handleRestoreVersion(v.version)}
                            disabled={isRestoringVersion}
                          >
                            {isRestoringVersion
                              ? t("documentEditor.restoring")
                              : t("documentEditor.restore")}
                          </button>
                        )}
                      </div>
                      {v.changesSummary && !v.isCurrent && (
                        <div className="version-history-item-summary">
                          {v.changesSummary}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Document Outline sidebar (standalone — comments & track changes now use floating cards) */}
        {showOutlinePanel && editorRef.current?.getEditor() && (
          <div className="tiptap-sidebar">
            <DocumentOutlinePanel
              editor={editorRef.current.getEditor()!}
              onClose={() => setShowOutlinePanel(false)}
            />
          </div>
        )}
      </div>

      {/* Status Bar */}
      <div className="tiptap-document-pane-status">
        {isAgentWorking ? (
          <span className="editor-locked-notice">
            {t("documentEditor.chatInProgressEditorLocked")}
          </span>
        ) : (
          lastSaved && (
            <span className="last-saved">
              {t("documentEditor.lastSaved")} {lastSaved.toLocaleTimeString()}
            </span>
          )
        )}
        <span className="doc-id">ID: {docId}</span>
      </div>

      {/* Text Stats Modal */}
      {showStats && textStats && (
        <div
          className="tiptap-stats-modal-overlay"
          onClick={() => setShowStats(false)}
        >
          <div
            className="tiptap-stats-modal"
            onClick={e => e.stopPropagation()}
          >
            <h5>{t("documentEditor.textStatistics")}</h5>
            <table>
              <tbody>
                <tr>
                  <td>{t("documentEditor.characters")}</td>
                  <td>{textStats.characters.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>{t("documentEditor.charactersNoSpacesLabel")}</td>
                  <td>{textStats.charactersNoSpaces.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>{t("documentEditor.wordsLabel")}</td>
                  <td>{textStats.words.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>{t("documentEditor.sentencesLabel")}</td>
                  <td>{textStats.sentences.toLocaleString()}</td>
                </tr>
                <tr>
                  <td>{t("documentEditor.paragraphsLabel")}</td>
                  <td>{textStats.paragraphs.toLocaleString()}</td>
                </tr>
              </tbody>
            </table>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setShowStats(false)}
            >
              {t("documentEditor.close")}
            </Button>
          </div>
        </div>
      )}

      {/* Restore Version Confirmation Modal */}
      {confirmRestoreVersion !== null && (
        <div
          className="version-restore-modal-overlay"
          onClick={() => setConfirmRestoreVersion(null)}
        >
          <div
            className="version-restore-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="version-restore-modal-header">
              <h5>{t("documentEditor.restoreVersion")}</h5>
            </div>
            <div className="version-restore-modal-body">
              <p>
                {t("documentEditor.restoreConfirmation", {
                  version: confirmRestoreVersion,
                })}
              </p>
              <p className="version-restore-modal-note">
                {t("documentEditor.restoreNote")}
              </p>
            </div>
            <div className="version-restore-modal-footer">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => setConfirmRestoreVersion(null)}
              >
                {t("documentEditor.cancel")}
              </Button>
              <Button size="sm" variant="primary" onClick={confirmRestore}>
                {t("documentEditor.restore")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipTapDocumentPane;
